import { useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect } from 'react';

import { db } from '../../../config';
import { AiAgentConfig } from './types';

export const AGENT_CONFIG_DOC_ID = 'config';

/**
 * Realtime'owy odczyt dokumentu `hotels/{organizationId}/aiagents/config`.
 *
 * Zwraca `null` gdy konfiguracja jeszcze nie istnieje — UI powinien wtedy
 * pokazać formularz tworzenia. Gdy istnieje — formularz edycji.
 */
export const useGetAiAgentConfig = (
  organizationId: string | null | undefined,
  options?: UseQueryOptions<AiAgentConfig | null, FirebaseError>,
): UseQueryResult<AiAgentConfig | null, FirebaseError> => {
  const queryClient = useQueryClient();
  const queryKey = ['aiagents-config', organizationId] as const;

  useEffect(() => {
    if (!organizationId) return undefined;

    const ref = doc(db, 'hotels', organizationId, 'aiagents', AGENT_CONFIG_DOC_ID);

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (!snapshot.exists()) {
          queryClient.setQueryData(queryKey, null);
          return;
        }
        const data = snapshot.data() as Partial<AiAgentConfig>;
        const parsed: AiAgentConfig = {
          name: data.name ?? '',
          gender: (data.gender as AiAgentConfig['gender']) ?? 'man',
          avatarId: data.avatarId ?? 'man-1',
          customAvatarUrl: typeof data.customAvatarUrl === 'string' ? data.customAvatarUrl : undefined,
          style: (data.style as AiAgentConfig['style']) ?? 'concise',
          tone: (data.tone as AiAgentConfig['tone']) ?? 'friendly',
          useEmoji: data.useEmoji ?? false,
          rules: Array.isArray(data.rules) ? data.rules : [],
          customPersonaPrompt: typeof data.customPersonaPrompt === 'string' ?
            data.customPersonaPrompt :
            undefined,
          excludedModules: Array.isArray(data.excludedModules) ?
            data.excludedModules.filter((m): m is string => typeof m === 'string') :
            [],
          excludedOutletIds: Array.isArray(data.excludedOutletIds) ?
            data.excludedOutletIds.filter((id): id is string => typeof id === 'string') :
            [],
          isActive: data.isActive ?? true,
          createdAt: data.createdAt as number | undefined,
          updatedAt: data.updatedAt as number | undefined,
        };
        queryClient.setQueryData(queryKey, parsed);
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('[useGetAiAgentConfig] error', error);
        queryClient.setQueryData(queryKey, null);
      },
    );

    return () => unsubscribe();
  }, [organizationId, queryClient]);

  return useQuery<AiAgentConfig | null, FirebaseError>({
    queryKey,
    queryFn: async () => queryClient.getQueryData<AiAgentConfig | null>(queryKey) ?? null,
    enabled: !!organizationId,
    staleTime: Infinity,
    retry: 0,
    ...options,
  });
};
